import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calculator, PoundSterling } from "lucide-react";

interface PaymentCalculatorProps {
  userType?: 'customer' | 'tradesman';
}

export function PaymentCalculator({ userType = 'customer' }: PaymentCalculatorProps) {
  const [quote, setQuote] = useState<string>('500');
  
  const quoteAmount = parseFloat(quote) || 0;
  const bookingFee = Math.round(quoteAmount * 0.03 * 100) / 100;
  const deposit = Math.round(quoteAmount * 0.07 * 100) / 100;
  const upfrontTotal = bookingFee + deposit;
  const directPayment = Math.round(quoteAmount * 0.93 * 100) / 100;
  const customerTotal = quoteAmount + bookingFee;

  const handleQuoteChange = (value: string) => {
    // Only allow numbers and decimal points
    const numericValue = value.replace(/[^0-9.]/g, '');
    setQuote(numericValue);
  };

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-background">
      <CardHeader className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Calculator className="w-5 h-5 text-primary" />
          <CardTitle className="text-lg">Payment Calculator</CardTitle>
        </div>
        <CardDescription>
          Enter any quote amount to see the payment breakdown
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="quote-input">Quote Amount (£)</Label>
          <div className="relative">
            <PoundSterling className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="quote-input"
              type="text"
              value={quote}
              onChange={(e) => handleQuoteChange(e.target.value)}
              className="pl-8 text-lg font-semibold"
              placeholder="500"
            />
          </div>
        </div>

        {quoteAmount > 0 && (
          <div className="space-y-3 pt-2 border-t">
            {userType === 'customer' ? (
              <>
                <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg">
                  <span className="text-sm font-medium">Upfront to Platform:</span>
                  <span className="font-bold text-primary">£{upfrontTotal.toFixed(2)}</span>
                </div>
                <div className="text-xs text-muted-foreground pl-3 -mt-2">
                  £{bookingFee.toFixed(2)} booking fee + £{deposit.toFixed(2)} deposit
                </div>
                
                <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                  <span className="text-sm font-medium">Direct to Tradesman:</span>
                  <span className="font-bold text-green-700 dark:text-green-400">£{directPayment.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg border-2 border-dashed border-primary/20">
                  <span className="text-sm font-bold">Total You Pay:</span>
                  <span className="font-bold text-lg">£{customerTotal.toFixed(2)}</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                  <span className="text-sm font-medium">You Receive from Customer:</span>
                  <span className="font-bold text-green-700 dark:text-green-400">£{directPayment.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                  <span className="text-sm font-medium">Platform Fee (10%):</span>
                  <span className="font-bold text-orange-700 dark:text-orange-400">£{upfrontTotal.toFixed(2)}</span>
                </div>
                <div className="text-xs text-muted-foreground pl-3 -mt-2">
                  £{bookingFee.toFixed(2)} booking fee + £{deposit.toFixed(2)} deposit from your quote
                </div>
                
                <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg border-2 border-dashed border-primary/20">
                  <span className="text-sm font-bold">Your Quote:</span>
                  <span className="font-bold text-lg">£{quoteAmount.toFixed(2)}</span>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}